import React, { useCallback } from "react";
import {
  RadioGroup,
  Radio,
} from "../components/wombat-ui";
import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer,
  ModalCloseButton,
} from '@chakra-ui/react';
import { useForm } from "react-hook-form";

import useTranslations from "../hooks/useTranslations";

import useNotification from "../hooks/useNotification";
import { ReportTypeEnum, useCreateReport } from '../graphql/generated/definitions';

type CreateReportModalProps = {
  reviewId: string;
  open: boolean;
  onClose: VoidFunction;
};

const CreateReportModal: React.FC<CreateReportModalProps> = ({
  reviewId,
  open,
  onClose,
}) => {
  const t = useTranslations();
  const [createReport] = useCreateReport();

  const { notifySuccess, notifyError } = useNotification();

  const handleCreateReport = useCallback(
    async (data) => {
      try {
        await createReport({
          variables: {
            input: {
              ...data,
            },
          },
        });
        notifySuccess({
          title: t("reports.notification.create.success"),
        });
      } catch {
        notifyError({
          title: t("reports.notification.create.error"),
        });
      }
      onClose();
    },
    [createReport]
  );

  const { handleSubmit, register } = useForm<{
    reviewId: string;
    type: ReportTypeEnum;
  }>({
    defaultValues: {
      reviewId,
    },
  });

  return (
    <Modal
      isOpen={open}
      aria-label="Create report form"
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(handleCreateReport)} id="createReportForm">
          <ModalHeader>
            {t("reports.sendNow")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <input type="hidden" name="reviewId" ref={register()} />
            <RadioGroup
              title={t("reports.reportType") as string}
              description={t("reports.reportTypeDescription")}
            >
              {["FirstReport", "IntermediateReport", "FinalReport"].map(
                (
                  reportType: "FirstReport" | "IntermediateReport" | "FinalReport"
                ) => (
                  <Radio
                    key={reportType}
                    id={`reportType${reportType}`}
                    label={t(`reports.header.${reportType}` as const) as string}
                    name={"type"}
                    value={reportType}
                    ref={register()}
                  />
                )
              )}
            </RadioGroup>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button
                onClick={onClose}
              >
                {t("forms.actions.cancel")}
              </Button>

              <Spacer />

              <Button
                type="submit"
                form="createReportForm"
                colorScheme="blue"
              >
                {t("forms.actions.send")}
              </Button>
            </HStack>
          </ModalFooter>
          </form>
      </ModalContent>
    </Modal>
  );
};

export default CreateReportModal;
